import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { listOrgTemplates, forkOrgTemplate, listTeams } from '../api'
import Logo from '../components/Logo'

interface OrgTemplate {
  name: string
  title: string
  description: string
  status: string
  version: string
  tags: string[]
  fork_count: number
  scope: string
}

export default function OrgTemplatesPage() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const [templates, setTemplates] = useState<OrgTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [forking, setForking] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [teamName, setTeamName] = useState('')

  useEffect(() => {
    listOrgTemplates().then(r => setTemplates(r.data || [])).catch(() => {}).finally(() => setLoading(false))
    listTeams().then(r => {
      const t = Array.isArray(r.data) ? r.data : (r.data?.data ?? [])
      if (t.length) setTeamName(t[0].name)
    }).catch(() => {})
  }, [])

  const filtered = templates.filter(t => {
    if (!search) return true
    return t.title.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase())
  })

  const handleFork = async (t: OrgTemplate) => {
    setForking(t.name)
    try {
      const r = await forkOrgTemplate(t.name)
      navigate(`/templates/${t.name}/build`, { state: { skillName: r.data.skill_name, sections: r.data.sections, templateTitle: t.title } })
    } catch (e: any) {
      setToast(e.response?.data?.detail || 'Failed to fork')
      setTimeout(() => setToast(''), 3000)
    } finally {
      setForking(null)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text1)' }}>
      {/* Nav */}
      <nav style={{
        height: 56, display: 'flex', alignItems: 'center', padding: '0 24px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}><Logo /></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginLeft: 32 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>Dashboard</button>
          <button onClick={() => navigate('/templates')} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>Public templates</button>
          <button style={{ background: 'none', border: 'none', color: 'var(--text1)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Org templates</button>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate('/templates')} style={{
          background: 'none', border: '1px solid var(--border)', borderRadius: 8,
          padding: '6px 14px', color: 'var(--text2)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
        }}>Browse public templates</button>
      </nav>

      {/* Header */}
      <div style={{ textAlign: 'center', padding: '48px 24px 32px', maxWidth: 640, margin: '0 auto' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#0d9488' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 1 }}>Org Templates</span>
        </div>
        <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 28, fontWeight: 700, letterSpacing: -0.5, marginBottom: 8 }}>
          {teamName || 'Your team'} templates
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 24 }}>
          Skills shared by your team. Fork, customize, and make them your own.
        </p>
        <div style={{ position: 'relative', maxWidth: 400, margin: '0 auto' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search org templates..."
            style={{
              width: '100%', padding: '10px 16px 10px 40px', borderRadius: 10,
              background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text1)',
              fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Cards */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 80px' }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, height: 180, animation: 'skelpulse 1.5s ease infinite', animationDelay: `${i * 0.1}s` }} />
            ))}
            <style>{`@keyframes skelpulse { 0%,100% { opacity: 0.4 } 50% { opacity: 0.8 } }`}</style>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 12 }}>
              {search ? 'No templates match your search.' : 'No org templates yet.'}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text3)' }}>
              Team members can share skills as templates from the three-dot menu on any skill.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {filtered.map(t => (
              <div key={t.name} style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
                padding: 24, display: 'flex', flexDirection: 'column', gap: 12,
                transition: 'border-color 0.2s, transform 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(13,148,136,0.4)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none' }}
              >
                {/* Badge */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: 'rgba(13,148,136,0.1)', color: '#0d9488' }}>
                    Org template
                  </span>
                  {t.status === 'production' && (
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>Production</span>
                  )}
                </div>
                {/* Title + desc */}
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 4 }}>{t.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {t.description}
                  </div>
                </div>
                {/* Footer */}
                <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {t.fork_count > 0 ? (
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>Used by {t.fork_count} member{t.fork_count !== 1 ? 's' : ''}</span>
                  ) : <span />}
                  <button
                    onClick={() => handleFork(t)}
                    disabled={forking === t.name}
                    style={{
                      background: 'none', border: 'none', color: '#0d9488', fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      opacity: forking === t.name ? 0.5 : 1,
                    }}
                  >
                    {forking === t.name ? 'Adding...' : 'Use template'} &rarr;
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, zIndex: 400,
          background: 'var(--text1)', color: 'var(--bg)', whiteSpace: 'nowrap',
        }}>
          {toast}
        </div>
      )}
    </div>
  )
}
